import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Search, ShieldCheck, RefreshCw } from "lucide-react";

const ENTITIES = ["all", "resident", "fee", "expense", "bed", "room", "floor", "building", "user"];

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = entity && entity !== "all" ? `?entity_type=${entity}&limit=500` : "?limit=500";
      const { data } = await api.get(`/audit${params}`);
      setRows(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [entity]); // eslint-disable-line

  const filtered = rows.filter((r) => {
    const t = q.toLowerCase();
    return !t || [r.user_name, r.action, r.entity_type, r.entity_id, r.reason]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(t));
  });

  const actionBadge = (a) => {
    if (a === "delete") return "destructive";
    if (a === "create") return "default";
    if (a.startsWith("status:")) return "secondary";
    if (a === "dropout" || a === "archive") return "destructive";
    if (a === "restore") return "default";
    return "outline";
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <div className="eyebrow text-muted-foreground">Compliance</div>
            <h1 className="font-display text-4xl font-bold mt-1">Audit Log</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Every create, edit, delete, archive, restore and dropout — with actor, IP, before/after and reason.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="audit-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <Card className="p-4 border-border bg-white">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-[200px]" data-testid="audit-entity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ENTITIES.map((e) => (
                <SelectItem key={e} value={e}>{e === "all" ? "All entities" : e.charAt(0).toUpperCase() + e.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Filter by user, action, entity, reason..." className="pl-9"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   data-testid="audit-search" />
          </div>
          <div className="ml-auto text-sm text-muted-foreground">{filtered.length} entries</div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="text-left text-muted-foreground eyebrow border-b border-border">
                <th className="py-3 pr-4">When</th>
                <th className="py-3 pr-4">User</th>
                <th className="py-3 pr-4">Role</th>
                <th className="py-3 pr-4">Action</th>
                <th className="py-3 pr-4">Entity</th>
                <th className="py-3 pr-4">Reason</th>
                <th className="py-3 pr-4">IP</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="py-3 pr-4 font-mono text-xs whitespace-nowrap">
                    {new Date(r.at).toLocaleString()}
                  </td>
                  <td className="py-3 pr-4 font-medium">{r.user_name}</td>
                  <td className="py-3 pr-4">
                    <Badge variant="secondary" className="uppercase text-[10px]">{r.user_role}</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <Badge variant={actionBadge(r.action)} className="text-xs">{r.action}</Badge>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="capitalize">{r.entity_type}</span>
                    <span className="text-xs text-muted-foreground font-mono ml-1">
                      {r.entity_id?.slice(0, 8)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground truncate max-w-[180px]">{r.reason || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{r.ip || "—"}</td>
                  <td className="py-3 pr-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => setDetail(r)}
                            data-testid={`audit-detail-${r.id}`}>Diff</Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No audit entries.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl capitalize">
                  {detail.action} — {detail.entity_type}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="eyebrow text-muted-foreground mb-1">When</div><div className="font-mono text-xs">{new Date(detail.at).toLocaleString()}</div></div>
                <div><div className="eyebrow text-muted-foreground mb-1">User</div><div>{detail.user_name} <span className="text-xs text-muted-foreground">({detail.user_role})</span></div></div>
                <div><div className="eyebrow text-muted-foreground mb-1">IP</div><div className="font-mono text-xs">{detail.ip || "—"}</div></div>
                <div><div className="eyebrow text-muted-foreground mb-1">Reason</div><div>{detail.reason || "—"}</div></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="eyebrow text-destructive mb-2">Previous</div>
                  <pre className="text-[11px] bg-destructive/5 border border-destructive/20 rounded-md p-3 overflow-x-auto max-h-96">
                    {detail.previous ? JSON.stringify(detail.previous, null, 2) : "—"}
                  </pre>
                </div>
                <div>
                  <div className="eyebrow text-primary mb-2">New</div>
                  <pre className="text-[11px] bg-primary/5 border border-primary/20 rounded-md p-3 overflow-x-auto max-h-96">
                    {detail.new ? JSON.stringify(detail.new, null, 2) : "—"}
                  </pre>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
